import { IProposalStatus, IProposalType } from "../common/enums/common.enums";
import { Metadata } from "@metaplex-foundation/mpl-token-metadata";
import {
  PublicKey,
  Connection,
  SimulatedTransactionResponse,
} from "@solana/web3.js";
import { ClubType, IClubProposals } from "../common/interfaces/club.interface";
import {
  IPieData,
  TransactionError,
} from "../common/interfaces/common.interface";
import { IProposalOverview } from "../common/interfaces/proposal.interface";
import { StakingStatus } from "../common/enums/club.enums";
import { MessageSignerWalletAdapterProps } from "@solana/wallet-adapter-base";
import {
  TRANSACTION_ERROR_IDENTIFIER,
  TRANSFER_ERROR_IDENTIFIER,
  GOVERNANCE_ERROR_IDENTIFIER,
  MESSAGE_TYPE,
} from "./constants";
import { NotificationManager } from "react-notifications";

export const getClubType = (value: ClubType) => {
  switch (value) {
    case ClubType.DepositBased:
      return "Deposit based";
    case ClubType.NftBased:
      return "NFT based";
    case ClubType.RoleBased:
      return "Role based";
    default:
      return "Deposit based";
  }
};

export const getStakeStatus = (value: StakingStatus) => {
  switch (value) {
    case StakingStatus.Canceled:
      return "Cancelled";
    case StakingStatus.Finished:
      return "Finished";
    case StakingStatus.Funding:
      return "Funding";
    case StakingStatus.InProgress:
      return "InProgress";
  }
};

export function getTrimmedPublicKey(publicKey: string): string {
  const publicKeyString = publicKey;
  return (
    publicKeyString.substring(0, 5) +
    "..." +
    publicKeyString.substring(publicKeyString.length - 5)
  );
}

export const generateProposalStatusPieData = (
  proposal: IClubProposals | IProposalOverview
): IPieData => {
  return {
    labels: [
      IProposalStatus.voting,
      IProposalStatus.executing,
      IProposalStatus.succedeed,
      IProposalStatus.cancelled,
      IProposalStatus.completed,
      IProposalStatus.defeated,
    ],
    datasets: [
      {
        label: "Proposals by status",
        data: Object.keys(proposal.proposalStatus).map(
          (key) => proposal.proposalStatus[key]
        ),
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(255, 206, 86, 0.2)",
          "rgba(75, 192, 192, 0.2)",
          "rgba(153, 102, 255, 0.2)",
          "rgba(255, 159, 64, 0.2)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };
};

export const generateProposalTypePieData = (
  proposal: IClubProposals | IProposalOverview
): IPieData => {
  return {
    labels: [
      IProposalType.discussion,
      IProposalType.trading,
      IProposalType.withdrawal,
    ],
    datasets: [
      {
        label: "Proposals by type",
        data: Object.keys(proposal.proposalType).map(
          (key) => proposal.proposalType[key]
        ),
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(255, 206, 86, 0.2)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };
};

/**
 * Get mint metadata
 * @param mintMetadataAccount {PublicKey} Club Address
 * @return {Metadata}
 */
export async function getMintMetadata(
  mintMetadataAccount: PublicKey
): Promise<any> {
  try {
    const RPC_CONNECTION = new Connection(
      "https://devnet.genesysgo.net",
      "confirmed"
    );
    const tokenMetaPubkey = await Metadata.getPDA(mintMetadataAccount);
    const metaData = await Metadata.load(RPC_CONNECTION, tokenMetaPubkey);
    const dataMetadata = await fetch(metaData!.data.data.uri);
    return await dataMetadata.json();
  } catch (err) {
    console.log(err);
    throw err;
  }
}

export const getProposalMaxVotingTime = (maxVotingTime: number) => {
  return Math.floor(maxVotingTime / (3600 * 24));
};

export function round(num, decimalPlaces = 9) {
  var p = Math.pow(10, decimalPlaces);
  var n = num * p * (1 + Number.EPSILON);
  return Math.round(n) / p;
}

export const signTransactionMessage = (
  messageText: string,
  signMessage: MessageSignerWalletAdapterProps["signMessage"] | undefined
) => {
  if (!signMessage) throw new Error("Wallet does not support message signing!");

  const message = new TextEncoder().encode(messageText);
  return signMessage(message);
};

export const mapSolanaErrors = (
  simulateResult: SimulatedTransactionResponse | null,
  errorMessage: string,
  txid: string
) => {
  const errorIndetifiers = [
    TRANSACTION_ERROR_IDENTIFIER,
    TRANSFER_ERROR_IDENTIFIER,
    GOVERNANCE_ERROR_IDENTIFIER,
  ];

  if (simulateResult && simulateResult.err) {
    if (simulateResult.logs) {
      errorIndetifiers.forEach((item) => {
        throwTransactionErrorIfNeeded(simulateResult.logs!, item, txid);
      });
    }
  }

  throw new TransactionError(errorMessage, txid);
};

const throwTransactionErrorIfNeeded = (
  simulateResultLogs: string[],
  errorIndetifier: string,
  txid: string
) => {
  const hassIdentifiedError = findFirstSubstring(
    simulateResultLogs,
    errorIndetifier
  );

  if (hassIdentifiedError !== -1) {
    throw new TransactionError(
      simulateResultLogs[hassIdentifiedError].split(errorIndetifier)[1],
      txid
    );
  }
};

export const findFirstSubstring = (array: string[], queryParameter: string) => {
  for (let i = 0; i < array.length; i++) {
    if (array[i].indexOf(queryParameter) !== -1) return i;
  }
  return -1;
};

export const createNotification = (
  messageType: string,
  messageText: string,
  description?: string
) => {
  switch (messageType) {
    case MESSAGE_TYPE.SUCCESS:
      NotificationManager.success(messageText);
      break;
    case MESSAGE_TYPE.ERROR:
      NotificationManager.error(description, messageText);
      break;
  }
};
